<template>
    <v-container class="py-7">
        <v-row>
            <v-col cols="12">
                <div class="title-container">
                    <div class="title-block">
                        <span class="page-title">{{ page.title }}</span>

                        <v-breadcrumbs class="app-breadcrumb" divider="›" :items="breadcrumbItems" />
                    </div>

                    <div class="button-group">
                        <v-btn v-if="$check('Creditors', 'PATCH')" color="primary" elevation="0" :loading="loading"
                            @click="save">
                            Salvar
                        </v-btn>

                        <v-btn color="primary" outlined elevation="0" @click="goBack">
                            Voltar
                        </v-btn>
                    </div>
                </div>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-card class="card-form-outlined">
                    <v-card-title class="card-group-title">Dados do crédito</v-card-title>
                    <v-card-text>
                    <v-form ref="form">
                        <v-row>
                            <v-col cols="12">
                                <v-text-field v-model="form.name" label="Nome" outlined dense hide-details="auto"
                                    :rules="[rules.required]" />
                            </v-col>
                        </v-row>
    
                        <v-row align="center">
                            <v-col cols="12" md="6">
                                <v-text-field v-if="form.noDocument" dense id="registration" label="CPF/CNPJ"
                                    :value="form.name" disabled outlined hide-details="auto" />
                                <v-text-field v-else dense id="registration" label="CPF/CNPJ" v-model="form.document"
                                    required
                                    v-mask="((form.document || '').replace(/\D/g, '').length <= 11) ? this.$masks.cpf : this.$masks.cnpj"
                                    :rules="[$rules.required, ((form.document || '').replace(/\D/g, '').length <= 11) ? this.$rules.cpf : this.$rules.cnpj]"
                                    outlined hide-details="auto" />
                            </v-col>
    
                            <v-col cols="12" md="6">
                                <v-checkbox v-model="form.noDocument" label="Sem CPF/CNPJ" dense hide-details />
                            </v-col>
                        </v-row>
    
                        <v-row v-if="form.noDocument">
                            <v-col cols="12">
                                <v-alert dense outlined type="warning" class="mb-0">
                                    O registro será salvo sem CPF/CNPJ válido. O nome do credor será usado como identificador temporário.
                                </v-alert>
                            </v-col>
                        </v-row>
    
                        <v-row>
                            <v-col cols="12" md="6">
                                <v-text-field v-model="form.email" label="Email" outlined dense hide-details="auto"
                                    :rules="[$rules.email]" />
                            </v-col>
    
                            <v-col cols="12" md="6">
                                <v-currency-field class="mb-3" dense id="amount" type="number" label="Valor"
                                    v-model="form.amount" outlined hide-details="auto" :allowNegative=false>
                                </v-currency-field>
    
                            </v-col>
    
                            <!-- <v-col cols="12" md="6">
                                        <v-text-field v-model="form.phone" label="Telefone" outlined dense
                                            hide-details="auto" />
                                    </v-col> -->
                        </v-row>
    
                        <v-row>
                            <v-col cols="12" md="4">
                                <v-autocomplete v-model="form.client" label="Caso" :items="clientList" outlined dense
                                    item-text="name" item-value="id" hide-details="auto" :rules="[rules.required]" />
                            </v-col>
    
                            <v-col cols="12" md="4">
                                <v-select v-model="form.class" label="Classe" :items="classList" outlined dense
                                    hide-details="auto" :rules="[rules.required]" />
                            </v-col>
    
                            <v-col cols="12" md="4">
                                <v-select v-model="form.version" label="Versão do QGC" :items="versionOptions"
                                    item-text="label" item-value="id" outlined dense hide-details="auto"
                                    :rules="[rules.required]" />
                            </v-col>
    
                        </v-row>
                    </v-form>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <v-overlay :value="loading">
            <v-progress-circular indeterminate size="64" />
        </v-overlay>
    </v-container>
</template>

<script>
import axios from 'axios';
import claimClasses from '@/common/data/creditor-types.data.js';
import qgcVersions from '@/common/data/qgc-versions.data.js';

import string from '@/common/util/string.js';

const defaultQgcVersion = (qgcVersions[0] && qgcVersions[0].id) || 1;

export default {
    name: "QgcDetails",

    data: () => ({
        loading: false,

        page: {
            isNew: false,
            title: ""
        },

        form: {
            name: null,
            document: null,
            noDocument: false,
            email: null,
            phone: null,
            client: null,
            class: null,
            amount: null,
            version: defaultQgcVersion
        },

        rules: {
            required: v => !!v || "Campo obrigatório."
        },
        clientList: [],
        versionOptions: qgcVersions,
    }),

    computed: {
        breadcrumbItems() {
            return [
                { text: "Início", to: "/home", exact: true },
                { text: "QGC", to: "/qgc", exact: true },
                {
                    text: this.page.isNew ? "Incluir" : "Editar",
                    disabled: true
                }
            ];
        }
    },

    created() {
        this.setPage();
        this.load();
        this.loadAuxData();
    },

    methods: {
        setPage() {
            this.page.isNew = this.$route.params.id === "novo";
            this.page.title = this.page.isNew
                ? "Incluir QGC"
                : "Editar QGC";
        },

        async load() {
            if (this.page.isNew) return;

            this.loading = true;

            try {
                const response = await axios.get(`/api/admin/creditor-claims/${this.$route.params.id}`);
                const data = response.data.content || response.data;

                const registration = data.registration || null;
                const digitsOnly = (registration || '').replace(/\D/g, '');
                const isValidDocument = digitsOnly.length <= 11
                    ? this.$rules.cpf(registration) === true
                    : this.$rules.cnpj(registration) === true;

                this.form = {
                    name: data.name || null,
                    document: isValidDocument ? registration : null,
                    noDocument: !isValidDocument,
                    email: data.email || null,
                    // phone: data.phone || null,
                    client: data.client ? data.client.id : (data.client_id || null),
                    class: data.type || null,
                    amount: data.amount === null || data.amount === undefined ? null : data.amount,
                    version: data.version || defaultQgcVersion
                };

            } catch (error) {
                this.$dialog.notify.error(this.$codes(error) || 'Erro ao carregar registro', { position: 'top-right', timeout: 5000 });
                this.goBack();
            } finally {
                this.loading = false;
            }
        },

        async loadAuxData() {
            try {
                // load classes from static data
                this.classList = claimClasses.map(c => ({ text: c.name, value: c.id }))

                // load clients for the select
                const res = await axios.get('/api/clients?page=1&limit=500')
                this.clientList = res.data.content.data
            } catch (e) {
                console.error('Error loading auxiliary data', e)
            }
        },

        async save() {
            if (!this.$refs.form.validate()) return;

            this.loading = true;

            const payload = {
                name: this.form.name,
                registration: this.form.noDocument
                    ? this.form.name
                    : string.sanitizeCpfCnpj(this.form.document),
                email: this.form.email,
                client_id: this.form.client,
                type: this.form.class,
                amount: this.form.amount,
                version: this.form.version,
                no_document: !!this.form.noDocument
            };

            try {
                if (this.page.isNew) {
                    await axios.post('/api/admin/creditor-claims', payload);
                    this.$dialog.notify.success('Registro criado', { position: 'top-right', timeout: 3000 });
                } else {
                    await axios.patch(`/api/admin/creditor-claims/${this.$route.params.id}`, payload);
                    this.$dialog.notify.success('Registro atualizado', { position: 'top-right', timeout: 3000 });
                }

                this.goBack();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error) || 'Erro ao salvar', { position: 'top-right', timeout: 5000 });
            } finally {
                this.loading = false;
            }
        },

        goBack() {
            this.$router.push("/qgc");
        }
    }
};
</script>
